import { getFeatureValue } from '@core/State/FeatureFlags/helpers/getFeatureValue'
import { FeatureFlags } from '@mobi/settings/featureFlags'
import { store } from '@core/Store'
import { setSecureSkyUrl } from '../Store'
import { logError } from '@core/Utils'
import {
  getSkyVisionSecureUrl,
  type SkyVideoEntitlementDto,
} from '@core/Areas/SkyVideoPlayer/Components/VideoPlayer/api'

// Default legacy URLs that don't require Akamai tokens
export const DEFAULT_SKY1_URL = 'https://skyliverwwa.akamaized.net/hls/live/2036963/sky1/index.m3u8'
export const DEFAULT_SKY2_URL = 'https://skyliverwwa.akamaized.net/hls/live/2036964/sky2/index.m3u8'

// New URLs with Akamai token support
const NEW_SKY1_URL = 'https://skyliverwwatk.akamaized.net/cmaf/live/2030963/sky1/index.m3u8'
const NEW_SKY2_URL = 'https://skyliverwwatk.akamaized.net/cmaf/live/2030964/sky2/index.m3u8'

/**
 * Gets the base URL for a Sky channel from LaunchDarkly or defaults
 */
function getBaseUrlForChannel(channel: 'Sky1' | 'Sky2'): string {
  const useNewUrls = getFeatureValue(FeatureFlags.SKY_VISION_STREAMS.key)

  if (useNewUrls) {
    return channel === 'Sky1' ? NEW_SKY1_URL : NEW_SKY2_URL
  }

  return channel === 'Sky1' ? DEFAULT_SKY1_URL : DEFAULT_SKY2_URL
}

/**
 * Checks if a URL is a default legacy URL that doesn't need securing
 */
function isDefaultUrl(url: string, channel: 'Sky1' | 'Sky2'): boolean {
  const defaultUrl = channel === 'Sky1' ? DEFAULT_SKY1_URL : DEFAULT_SKY2_URL
  return url === defaultUrl
}

/**
 * Checks if we have a valid cached secure URL
 */
function hasValidCachedUrl(channel: 'Sky1' | 'Sky2'): boolean {
  const state = store.getState()
  const existingUrl =
    channel === 'Sky1' ? state.skyVideoPlayer.sky1SecureUrl : state.skyVideoPlayer.sky2SecureUrl
  const expiry = state.skyVideoPlayer.secureUrlExpiry

  return !!(existingUrl && expiry && Date.now() < expiry)
}

/**
 * Fetches secure URL from backend API
 */
async function fetchSecureUrlFromApi(baseUrl: string): Promise<SkyVideoEntitlementDto> {
  return getSkyVisionSecureUrl(baseUrl)
}

/**
 * Main function to fetch and store secure Sky URL
 * Handles:
 * - Default URLs (no securing needed)
 * - LaunchDarkly feature flag URLs
 * - Caching logic
 * - API calls for new streams
 */
export async function fetchAndStoreSkySecureUrl(channel: 'Sky1' | 'Sky2'): Promise<void> {
  try {
    const baseUrl = getBaseUrlForChannel(channel)

    // CASE 1: Default URL - no securing needed, just store it
    if (isDefaultUrl(baseUrl, channel)) {
      store.dispatch(
        setSecureSkyUrl({
          channel,
          secureUrl: baseUrl,
        })
      )
      return
    }

    // CASE 2: New URL from LD - check if we have valid cached secure URL
    if (hasValidCachedUrl(channel)) {
      // Already have a valid token, no need to fetch
      return
    }

    // CASE 3: New URL needs securing - fetch from backend
    const result = await fetchSecureUrlFromApi(baseUrl)

    if (result.IsEntitled && result.SecureUri) {
      store.dispatch(
        setSecureSkyUrl({
          channel,
          secureUrl: result.SecureUri,
        })
      )
    } else {
      logError(
        `getSkyVisionSecureUrl: Not entitled or failed for channel=${channel}, baseUrl=${baseUrl}`
      )
    }
  } catch {
    logError(`fetchAndStoreSkySecureUrl: Failed to retrieve secure URL for channel=${channel}`)
    // On error, VideoPlayer will fall back to base URL from LaunchDarkly
  }
}
